/* ============================================================
   CULT RACING — COLOUR PICKER (portable)

   Renamed from antisocial-colors.js, unchanged in behaviour: it was
   already generic, keyed on .lot--colors[data-color-lot] and looping
   over every colour lot on the page. Use this, not a per-drop fork.

   Loads AFTER drop-page.js and touches nothing that file owns.
   Cart, add-to-cart, mobile auto-reveal and the deferred back image
   are all still drop-page.js's, unchanged, so this cannot break the
   money path on any other lot.

   It is small on purpose. Everything expensive is already in the
   DOM from Liquid: four real size keypads (already bound to the
   cart by drop-page.js at load) and every colour's image URLs on
   its swatch. This script only decides which one is showing.

   WHAT IT DELIBERATELY DOES NOT DO:
   - build size buttons (they would not be bound to the cart)
   - fetch anything on selection (the URLs are already here)
   - touch .lot elements that are not colour lots
   ============================================================ */
(function () {
  "use strict";

  var lots = [].slice.call(document.querySelectorAll(".lot--colors[data-color-lot]"));
  if (!lots.length) return;

  lots.forEach(function (lot) {
    var swatches = [].slice.call(lot.querySelectorAll(".color-btn"));
    var keypads  = [].slice.call(lot.querySelectorAll("[data-color-sizes]"));
    var front    = lot.querySelector("img.front");
    var back     = lot.querySelector("img.back");
    var prompt   = lot.querySelector("[data-size-prompt]");
    if (!swatches.length || !front) return;

    var handoffTimer = null;

    function select(btn, userInitiated) {
      var color = btn.dataset.color;

      swatches.forEach(function (b) {
        var on = b === btn;
        b.classList.toggle("is-active", on);
        b.setAttribute("aria-checked", on ? "true" : "false");
        // Roving tabindex: a radiogroup should be ONE tab stop, then arrow
        // keys within it. Four tab stops to get past one lot is hostile on a
        // 26-lot page.
        b.tabIndex = on ? 0 : -1;
      });

      keypads.forEach(function (k) { k.hidden = k.dataset.colorSizes !== color; });

      // Swap the artwork. srcset BEFORE src, or the browser commits to the
      // fallback before the candidate list exists — the same ordering rule
      // drop-page.js's deferred back images follow.
      if (btn.dataset.frontSrcset) front.srcset = btn.dataset.frontSrcset;
      if (btn.dataset.front) front.src = btn.dataset.front;
      front.alt = (btn.dataset.title || "") + " front";

      if (back) {
        // If drop-page.js has already promoted the back image, swap it live.
        // If it has not, update the data-* it will promote from later, so the
        // deferral keeps working and we never fetch a back nobody looked at.
        if (back.dataset.src || back.dataset.srcset) {
          if (btn.dataset.backSrcset) back.dataset.srcset = btn.dataset.backSrcset;
          if (btn.dataset.back) back.dataset.src = btn.dataset.back;
        } else {
          if (btn.dataset.backSrcset) back.srcset = btn.dataset.backSrcset;
          if (btn.dataset.back) back.src = btn.dataset.back;
        }
        back.alt = (btn.dataset.title || "") + " back";
      }

      // The lightbox reads the photo set from /products/<handle>.js using this
      // attribute, so it has to follow the selection or it opens the wrong
      // colour's photos.
      if (btn.dataset.handle) lot.dataset.handle = btn.dataset.handle;

      if (userInitiated) {
        // Hand the eye from step 1 to step 2, once. CSS does the animation and
        // honours prefers-reduced-motion; this only toggles the class.
        lot.classList.remove("just-picked");
        void lot.offsetWidth;                     // restart the animation
        lot.classList.add("just-picked");
        clearTimeout(handoffTimer);
        handoffTimer = setTimeout(function () { lot.classList.remove("just-picked"); }, 900);
        if (prompt) prompt.textContent = "Then your size";
      }
    }

    swatches.forEach(function (btn, i) {
      btn.tabIndex = btn.classList.contains("is-active") ? 0 : -1;

      btn.addEventListener("click", function (e) {
        // The lot is also a lightbox target and a reveal target; a colour tap
        // must not open a photo viewer on top of the thing being chosen.
        e.preventDefault();
        e.stopPropagation();
        select(btn, true);
      });

      // Arrow-key support, because role="radiogroup" promises it.
      btn.addEventListener("keydown", function (e) {
        var dir = e.key === "ArrowRight" || e.key === "ArrowDown" ? 1
                : e.key === "ArrowLeft"  || e.key === "ArrowUp"   ? -1 : 0;
        if (!dir) return;
        e.preventDefault();
        var next = swatches[(i + dir + swatches.length) % swatches.length];
        next.focus();
        select(next, true);
      });
    });

    // Preload the other colours' fronts once the lot is near the viewport, so
    // switching is instant rather than a visible reload. Fronts only: a back
    // print nobody has revealed is not worth the bytes on a 95%-mobile page.
    var preloaded = false;
    function preload() {
      if (preloaded) return;
      preloaded = true;
      swatches.forEach(function (b) {
        if (b.classList.contains("is-active") || !b.dataset.front) return;
        var im = new Image();
        if (b.dataset.frontSrcset) im.srcset = b.dataset.frontSrcset;
        im.src = b.dataset.front;
      });
    }
    if ("IntersectionObserver" in window) {
      var obs = new IntersectionObserver(function (entries) {
        entries.forEach(function (en) {
          if (!en.isIntersecting) return;
          preload();
          obs.unobserve(en.target);
        });
      }, { rootMargin: "600px 0px 600px 0px" });
      obs.observe(lot);
    } else {
      preload();
    }
    // Whichever comes first: a swatch the visitor is reaching for should never
    // wait on the observer.
    lot.addEventListener("pointerenter", preload, { once: true });
  });
})();

/* ============================================================
   SIZE GUIDE BUTTONS — injected here, on purpose.

   This page orders the rack by DESIGN, so it has no `.rack__rule`
   garment headers. havoc-size-guide.js finds a lot's garment by
   walking back to the nearest rule, which on this page finds
   nothing, so every lot would silently lose its Size guide button.

   This script is loaded BEFORE havoc-size-guide.js and injects the
   buttons from window.CR_LOT_GARMENT (a handle -> garment map the
   page snippet emits) plus data-garment on the merged colour lots.
   havoc-size-guide.js then:
     - skips its own injection, because it bails on any .lot__title
       that already contains a .sg-btn;
     - still wires the popup, because it collects `.sg-btn` AFTER
       the injection step and binds handlers to whatever it finds.
   So the shared file is untouched and HAVOC keeps working exactly
   as it did.

   The title text is wrapped in .lot__title-text with the same
   markup havoc-size-guide.js would have produced, because the CSS
   for the flex row and the load-bearing min-width:0 targets it.
   ============================================================ */
(function () {
  "use strict";
  var box = document.getElementById("sizeGuide");
  if (!box) return;
  var map = window.CR_LOT_GARMENT || {};

  function keyOf(label) {
    return label ? label.toLowerCase().replace(/\s+/g, "-") : null;
  }

  [].slice.call(document.querySelectorAll(".rack__grid .lot")).forEach(function (lot) {
    var label = lot.dataset.garment || map[lot.dataset.handle] || null;
    var key = keyOf(label);
    // no chart for this garment: no button, same as the shared file decides
    if (!key || !box.querySelector('[data-guide-panel="' + key + '"]')) return;

    var h = lot.querySelector(".lot__title");
    /* skip if the lot ALREADY has a button anywhere - it normally does now,
       rendered in Liquid in .lot__foot. Scoping this test to .lot__title
       missed that and injected a duplicate. */
    if (!h || lot.querySelector(".sg-btn")) return;

    // textContent both ways, so nothing is ever parsed as HTML
    var text = h.textContent;
    h.textContent = "";
    var span = document.createElement("span");
    span.className = "lot__title-text";
    span.textContent = text;

    var b = document.createElement("button");
    b.type = "button";
    b.className = "sg-btn sg-btn--lot";
    b.setAttribute("data-guide", key);
    b.setAttribute("data-guide-label", label);
    b.setAttribute("aria-label", label + " size guide");
    b.textContent = "Size guide";

    h.appendChild(span);
    h.appendChild(b);
  });
})();
